import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { Modal, Typography, useMediaQuery, useTheme } from '@mui/material';
import Loader from '@tcl-boron-prefabs/loader';
import { Button } from '@tcl-boron-prefabs/button';
import { IconButton } from '@tcl-boron-prefabs/icon-button';
import { ArrowLeft, Download as DownloadIcon } from '@tcl-boron-icons/icons';
import { GrayscalePalette } from '@tcl-boron-colors/colors';
import { createUseStyles } from 'react-jss';
import { PortalContainer } from '../../components/PortalContainer';
import ErrorScreen from '../../components/ErrorScreen';
import AttachmentViewer from '../../components/AttachmentViewer';
import usePortalContext from '../../context/usePortalContext';
import { PortalAttachmentContent } from '../../context/interfaces/attachment';
import { NavigationPath } from '../../../api/constants';
import triggerDownload from '../../utils/triggerDownload';
import logger from '../../../../utils/logger';

const useStyles = createUseStyles({
  page: {
    width: '100%',
    maxWidth: '960px',
    margin: '0 auto',
    padding: '16px 0 32px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
    gap: '16px',
    '@media (min-width: 600px)': {
      padding: '24px 0 48px',
      gap: '20px',
    },
  },
  titleRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  title: {
    wordBreak: 'break-word',
  },
  details: {
    width: '100%',
    backgroundColor: 'white',
    border: `1px solid ${GrayscalePalette[200]}`,
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  detailLabel: {
    color: GrayscalePalette[600],
    textTransform: 'uppercase',
    fontSize: '12px',
    fontWeight: 600,
  },
  detailValue: {
    color: GrayscalePalette[700],
    wordBreak: 'break-word',
  },
  viewerContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    flexGrow: 1,
  },
  centerState: {
    flexGrow: 1,
    minHeight: '320px',
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '512px',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '32px',
    gap: '16px',
  },
});

const formatDateTime = (value?: string) => {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return undefined;
  }
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const DocumentDetail: FunctionComponent = () => {
  const { fetchHelper } = usePortalContext();
  const { push } = useHistory();
  const { id } = useParams<{ id: string }>();
  const classes = useStyles();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  const [attachment, setAttachment] = useState<PortalAttachmentContent>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchAttachment = async () => {
      try {
        const response = await fetchHelper<PortalAttachmentContent>(`/attachment/${id}`);
        setAttachment(response);
      } catch (e) {
        logger.error(e as Error, 'Error fetching document');
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAttachment();
  }, [id]);

  const handleDownload = () => {
    if (!attachment) {
      return;
    }
    setShowModal(true);
  };

  const handleConfirmDownload = () => {
    if (!attachment) {
      return;
    }
    try {
      const cleanedBase64 = attachment.content.replace(/(\r\n|\n|\r)/gm, '');
      const binary = window.atob(cleanedBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: attachment.contentType });
      triggerDownload(blob, attachment.fileName);
    } catch (e) {
      logger.error(e as Error, 'Error downloading document');
    } finally {
      setShowModal(false);
    }
  };

  const detailItems = useMemo(() => {
    if (!attachment) {
      return [];
    }
    return [
      { label: 'File name', value: attachment.fileName },
      { label: 'Created', value: formatDateTime(attachment.created_at) },
    ].filter((item): item is { label: string; value: string } => Boolean(item.value));
  }, [attachment]);

  const renderContent = () => {
    if (loading) {
      return (
        <Grid
          container
          direction="column"
          gap="8px"
          alignItems="center"
          justifyContent="center"
          className={classes.centerState}
        >
          <Loader size="large" />
          <Typography variant="body1">Loading document</Typography>
        </Grid>
      );
    }

    if (error || !attachment) {
      return <ErrorScreen />;
    }

    return (
      <div className={classes.page}>
        {detailItems.length > 0 && (
          <dl className={classes.details} data-testid="attachment-details">
            {detailItems.map((item) => (
              <div key={item.label} className={classes.detailItem}>
                <Typography component="dt" className={classes.detailLabel}>
                  {item.label}
                </Typography>
                <Typography component="dd" variant="body2" className={classes.detailValue} sx={{ margin: 0 }}>
                  {item.value}
                </Typography>
              </div>
            ))}
          </dl>
        )}
        <div className={classes.viewerContainer}>
          <AttachmentViewer
            docId={attachment.id}
            doc={attachment.content}
            docType={attachment.contentType}
            scrollable
            maxHeight={isSmUp ? '78vh' : '70vh'}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal open={showModal} onClose={() => setShowModal(false)} data-testid="download-document-modal">
        <Grid container direction="column" className={classes.modal}>
          <Typography variant="h1">Confirm Download</Typography>
          <Typography variant="body1">
            Once you download this file, it will no longer be secured by Tempus. Other apps on your device might have
            access to this file.
          </Typography>
          <Typography variant="body2">Do you want to continue?</Typography>
          <Grid container justifyContent="space-between">
            <Button
              buttonType="tertiary"
              onClick={() => setShowModal(false)}
              ariaLabel="Cancel"
              data-testid="cancel-download-button"
            >
              <Typography variant="button">Cancel</Typography>
            </Button>
            <Button
              buttonType="primary"
              onClick={handleConfirmDownload}
              ariaLabel="Download"
              data-testid="confirm-download-button"
            >
              <Typography variant="button">Download</Typography>
            </Button>
          </Grid>
        </Grid>
      </Modal>
      <PortalContainer
        OverrideMenuElement={
          <IconButton name="Back" ariaLabel="Back" Icon={ArrowLeft} onClick={() => push(NavigationPath.DOCUMENTS)} />
        }
        HeaderElement={
          attachment ? (
            <IconButton
              data-testid="download-document-button"
              name="Download document"
              ariaLabel="Download document"
              Icon={DownloadIcon}
              onClick={handleDownload}
            />
          ) : undefined
        }
      >
        {renderContent()}
      </PortalContainer>
    </>
  );
};

export default DocumentDetail;
